import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { bool, func, object } from 'prop-types';
import classNames from 'classnames';
import { apiBaseUrl } from '../../util/api';
import io from 'socket.io-client';
import ChatbotListingCards from '../ChatbotListingCards/ChatbotListingCards';

import css from './ChatbotWidget.module.css';

const TYPING_INDICATOR_DELAY = 1000;
const MAX_MESSAGE_LENGTH = 1000;
const ASYNC_RESPONSE_TIMEOUT = 30000;
const ASYNC_POLL_INTERVAL = 3000;

// Suggested questions for users
const SUGGESTED_QUESTIONS = [
  {
    text: 'Find Animal Actors',
    action: 'Help me book animal actors',
  },
  {
    text: 'Find Pet Creators',
    action: 'Help me find pet creators',
  },
  {
    text: 'View Casting Calls',
    action: 'Find casting calls',
  },
];

const SEARCH_RESULTS_FOLLOW_UP_QUESTIONS = [
  {
    text: 'Refine Search',
    action: 'Help me refine my search',
  },
  {
    text: 'View More Results',
    action: 'Find more results',
  },
  {
    text: 'Start Over',
    action: 'Start over',
  },
];

const NO_RESULTS_FOLLOW_UP_QUESTIONS = [
  {
    text: 'Contact The Animal List',
    action: 'contact_the_animal_list',
  },
  {
    text: 'Post a Casting Call',
    action: 'post_a_casting_call',
  },
  {
    text: 'Start a New Search',
    action: 'start_a_new_search',
  },
];

// Human-readable progress labels for the agent's tool calls, so the user sees
// what the assistant is doing instead of an undifferentiated typing dot.
const TOOL_STATUS_LABELS = {
  search_listings: 'Searching listings…',
  lookup_knowledge: 'Checking the FAQ…',
  escalate_to_support: 'Contacting the team…',
};

const ROTATING_STATUS_MESSAGES = [
  'The Animal List is typing…',
  'Searching for relevant information…',
  'Still looking for results...',
  'Just a moment…',
  'Preparing your answer…',
];

/**
 * Stream one turn from POST /api/chatbot/stream.
 *
 * The endpoint returns Server-Sent Events over a normal POST, so EventSource
 * (GET-only) cannot be used — read the body stream and split on the SSE frame
 * delimiter instead.
 *
 * Returns true when the turn completed, false when streaming is unavailable and
 * the caller should fall back to the plain POST /message endpoint.
 */
const streamAgentTurn = async ({ sessionId, message, onEvent }) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = setTimeout(() => {
    if (controller) {
      console.warn('[ChatbotWidget] Stream turn timed out after 35s, aborting fetch');
      controller.abort();
    }
  }, 35000);

  try {
    const response = await fetch(`${apiBaseUrl()}/api/chatbot/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ sessionId, message }),
      signal: controller ? controller.signal : undefined,
    });

    if (!response.ok || !response.body || typeof response.body.getReader !== 'function') {
      return false;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let turnFinished = false;

    const handleFrame = frame => {
      const dataLines = frame
        .split('\n')
        .filter(line => line.startsWith('data:'))
        .map(line => line.slice(5).trim());

      if (!dataLines.length) return;

      try {
        const parsed = JSON.parse(dataLines.join(''));
        onEvent(parsed);
        if (
          parsed &&
          (parsed.type === 'message' || parsed.type === 'done' || parsed.type === 'error')
        ) {
          turnFinished = true;
        }
      } catch (error) {
        // A malformed frame should not abort the rest of the turn.
        console.warn('Ignoring unparseable chatbot stream frame');
      }
    };

    for (; ;) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        handleFrame(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');
      }

      if (turnFinished) {
        try {
          reader.cancel();
        } catch (e) { }
        break;
      }
    }

    if (buffer.trim()) handleFrame(buffer);
    return true;
  } catch (error) {
    console.warn(
      '[ChatbotWidget] Stream turn failed or aborted:',
      error?.name === 'AbortError' ? 'Timeout' : error?.message
    );
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Simple markdown parser for chatbot messages
const parseMarkdown = text => {
  if (!text) return '';

  let html = text;

  // Convert **bold** to <strong>
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Convert *italic* to <em>
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Convert numbered lists (1. item) - remove the number prefix
  html = html.replace(/^(\d+\.\s+)(.+)$/gm, '<li>$2</li>');
  html = html.replace(/(<li>.*<\/li>)/s, '<ol>$1</ol>');

  // Convert bullet lists (- item or * item)
  html = html.replace(/^[\-\*]\s+(.+)$/gm, '<li>$1</li>');

  // Convert line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br/>');

  // Wrap in paragraph if not already wrapped
  if (!html.startsWith('<')) {
    html = `<p>${html}</p>`;
  }

  return html;
};

const ChatbotWidget = ({ isOpen, onToggle, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [showStarterButtons, setShowStarterButtons] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingStatusIndex, setTypingStatusIndex] = useState(0);
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, messageIndex: null });
  const [feedbackForm, setFeedbackForm] = useState({ email: '', phone: '', message: '' });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState({});
  const [toolStatus, setToolStatus] = useState(null);
  const [socket, setSocket] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesRef = useRef([]);
  const asyncResponseTimeoutRef = useRef(null);
  const asyncResponsePollRef = useRef(null);
  const pendingAsyncRequestRef = useRef(null);

  useEffect(() => {
    let interval = null;
    if (isTyping) {
      setTypingStatusIndex(0);
      interval = setInterval(() => {
        setTypingStatusIndex(prev => (prev + 1) % ROTATING_STATUS_MESSAGES.length);
      }, 6000);
    } else {
      setTypingStatusIndex(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTyping]);

  const shouldShowStarterButtonsForHistory = historyMessages => {
    const hasUserInteraction = historyMessages.some(msg => msg.type === 'user');
    const hasSearchResults = historyMessages.some(msg =>
      (msg.attachments || []).some(attachment => attachment.type === 'listing_cards')
    );

    return !hasUserInteraction && !hasSearchResults;
  };

  const getContextualFollowUpQuestions = lastBotMessage => {
    if (!lastBotMessage || lastBotMessage.type !== 'bot') {
      return [];
    }

    if (lastBotMessage.followUpContext === 'search_results') {
      return SEARCH_RESULTS_FOLLOW_UP_QUESTIONS;
    }

    if (lastBotMessage.followUpContext === 'no_results') {
      return NO_RESULTS_FOLLOW_UP_QUESTIONS;
    }

    return [];
  };

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const clearAsyncResponseRecovery = () => {
    if (asyncResponseTimeoutRef.current) {
      clearTimeout(asyncResponseTimeoutRef.current);
      asyncResponseTimeoutRef.current = null;
    }

    if (asyncResponsePollRef.current) {
      clearInterval(asyncResponsePollRef.current);
      asyncResponsePollRef.current = null;
    }

    pendingAsyncRequestRef.current = null;
  };

  const pollForLatestResponse = async () => {
    if (!sessionId) {
      return;
    }

    try {
      const histResp = await fetch(`${apiBaseUrl()}/api/chatbot/history/${sessionId}`, {
        method: 'GET',
        credentials: 'include',
      });
      const histData = await histResp.json();

      if (!histData.success || !Array.isArray(histData.history)) {
        return;
      }

      const formattedHistory = histData.history.map(msg => ({
        type: msg.messageType,
        content: msg.content.text,
        attachments: msg.content.attachments || [],
        timestamp: new Date(msg.timestamp),
        followUpContext: msg.followUpContext || msg.botResponse?.followUpContext || null,
      }));

      const pendingRequest = pendingAsyncRequestRef.current;
      if (!pendingRequest) {
        return;
      }

      const latestBotMessage = [...formattedHistory].reverse().find(msg => msg.type === 'bot');
      const hasNewBotReply =
        latestBotMessage && latestBotMessage.timestamp > new Date(pendingRequest.sentAt);

      if (hasNewBotReply) {
        setMessages(formattedHistory);
        setShowStarterButtons(shouldShowStarterButtonsForHistory(formattedHistory));
        setIsTyping(false);
        clearAsyncResponseRecovery();
      }
    } catch (error) {
      // Keep polling silently until timeout expires.
    }
  };

  const startAsyncResponseRecovery = sentAt => {
    clearAsyncResponseRecovery();
    pendingAsyncRequestRef.current = {
      sentAt: sentAt || new Date().toISOString(),
    };

    asyncResponsePollRef.current = setInterval(() => {
      pollForLatestResponse();
    }, ASYNC_POLL_INTERVAL);

    // Run one immediate poll so fast webhook responses are shown quickly.
    pollForLatestResponse();

    asyncResponseTimeoutRef.current = setTimeout(() => {
      clearAsyncResponseRecovery();
      setIsTyping(false);
      addMessage(
        'bot',
        "I'm still working on that request. Please try again in a moment if you don't receive a response.",
        [],
        true
      );
    }, ASYNC_RESPONSE_TIMEOUT);
  };

  const getStorageKey = user => {
    try {
      if (user && user.id && user.id.uuid) return `chatbot_sessionId_${user.id.uuid}`;
    } catch (e) {
      // ignore
    }
    return 'chatbot_sessionId_anon';
  };

  // Initialize chatbot session
  useEffect(() => {
    if (isOpen && !sessionId) {
      initializeChatSession();
    }
  }, [isOpen, sessionId]);

  // Restore sessionId from localStorage if available (helps preserve context across reloads)
  useEffect(() => {
    const key = getStorageKey(currentUser);
    try {
      const stored = window.localStorage.getItem(key);
      if (stored && !sessionId) {
        // set it optimistically; initializeChatSession will validate/load history
        console.debug('Restoring chatbot sessionId from localStorage:', key, stored);
        setSessionId(stored);
      }
    } catch (err) {
      // ignore localStorage errors
      console.warn('Failed to read chatbot sessionId from localStorage', err);
    }
  }, [currentUser]);

  // Setup Socket.IO connection
  useEffect(() => {
    if (sessionId && !socket) {
      const socketUrl = apiBaseUrl();

      const joinChatSessionRoom = targetSessionId => {
        newSocket.emit('join_chat_session', targetSessionId, ack => {
          console.log('[Chatbot] join_chat_session ack:', ack);
        });
      };

      const newSocket = io(socketUrl, {
        withCredentials: true,
        transports: ['websocket', 'polling'], // Ensure fallback transports
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      newSocket.on('connect', () => {
        console.log('[Chatbot] Socket connected:', newSocket.id);
        joinChatSessionRoom(sessionId);
      });

      newSocket.on('reconnect', () => {
        console.log('[Chatbot] Socket reconnected:', newSocket.id);
        joinChatSessionRoom(sessionId);
      });

      newSocket.on('chat_session_joined', data => {
        console.log('[Chatbot] chat_session_joined event:', data);
      });

      newSocket.on('bot_response', data => {
        clearAsyncResponseRecovery();
        setIsTyping(false);
        addMessage(
          'bot',
          data.response,
          data.attachments || [],
          false,
          data.followUpContext || null
        );
      });

      newSocket.on('disconnect', reason => {
        console.log('[Chatbot] Socket.IO disconnected:', reason);
      });

      newSocket.on('connect_error', error => {
        console.error('[Chatbot] Socket.IO connection error:', {
          message: error?.message,
          description: error?.description,
          context: error?.context,
        });
      });

      setSocket(newSocket);

      return () => {
        console.log('Cleaning up Socket.IO connection for sessionId:', sessionId);
        clearAsyncResponseRecovery();
        if (newSocket) {
          newSocket.disconnect();
        }
      };
    }
  }, [sessionId]); // Removed socket dependency to prevent cleanup loops

  useEffect(() => {
    return () => {
      clearAsyncResponseRecovery();
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const initializeChatSession = async () => {
    try {
      setIsLoading(true);
      // If we already have a sessionId (restored from localStorage), try to load its history first
      if (sessionId) {
        try {
          const histResp = await fetch(`${apiBaseUrl()}/api/chatbot/history/${sessionId}`, {
            method: 'GET',
            credentials: 'include',
          });

          const histData = await histResp.json();
          if (histData.success) {
            const formattedHistory = histData.history.map(msg => ({
              type: msg.messageType,
              content: msg.content.text,
              attachments: msg.content.attachments || [],
              timestamp: new Date(msg.timestamp),
              followUpContext: msg.followUpContext || msg.botResponse?.followUpContext || null,
            }));
            // Server returns chronological order; keep it as-is
            setMessages(formattedHistory);
            setShowStarterButtons(shouldShowStarterButtonsForHistory(formattedHistory));
            // keep the sessionId as-is and exit early
            return;
          }
          // if history fetch failed or session not found, clear stored sessionId and fall back to init
          console.debug(
            'History fetch returned success=false for sessionId, clearing stored session and reinitializing',
            sessionId
          );
          try {
            window.localStorage.removeItem(getStorageKey(currentUser));
          } catch (e) { }
          setSessionId(null);
        } catch (err) {
          // ignore and fall back to init, but clear the stored session to avoid repeating bad id
          console.warn(
            'Error fetching history for sessionId, clearing stored session',
            sessionId,
            err
          );
          try {
            window.localStorage.removeItem(getStorageKey(currentUser));
          } catch (e) { }
          setSessionId(null);
        }
      }

      const response = await fetch(`${apiBaseUrl()}/api/chatbot/init`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: 'widget',
          userAgent: navigator.userAgent,
          platform: 'web',
        }),
      });

      const data = await response.json();

      if (data.success) {
        console.log('Setting sessionId to:', data.sessionId);
        setSessionId(data.sessionId);
        try {
          window.localStorage.setItem(getStorageKey(currentUser), data.sessionId);
        } catch (err) {
          // ignore
        }

        // Load conversation history
        if (data.history && data.history.length > 0) {
          const formattedHistory = data.history.map(msg => ({
            type: msg.messageType,
            content: msg.content.text,
            attachments: msg.content.attachments || [],
            timestamp: new Date(msg.timestamp),
            followUpContext: msg.followUpContext || msg.botResponse?.followUpContext || null,
          }));
          // Server returns chronological order
          setMessages(formattedHistory);
          setShowStarterButtons(shouldShowStarterButtonsForHistory(formattedHistory));
        } else {
          // Add welcome message with quick actions for new sessions
          setShowStarterButtons(true);
          console.log('Adding welcome message with quick actions:', data.quickActions);
          addMessage('bot', data.welcomeMessage, data.quickActions || []);
        }
      }
    } catch (error) {
      console.error('Failed to initialize chat session:', error);
      addMessage('bot', "Sorry, I'm having trouble connecting. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const addMessage = (
    type,
    content,
    attachments = [],
    noFeedback = false,
    followUpContext = null
  ) => {
    const hasSearchResults = (attachments || []).some(
      attachment => attachment.type === 'listing_cards'
    );
    if (type === 'user' || hasSearchResults) {
      setShowStarterButtons(false);
    }

    const newMessage = {
      type,
      content,
      attachments,
      timestamp: new Date(),
      noFeedback, // Flag to prevent feedback buttons on certain messages
      followUpContext,
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const normalizeMessageInput = value => {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (value && typeof value === 'object') {
      if (typeof value.message === 'string') return value.message;
      if (typeof value.text === 'string') return value.text;
      if (typeof value.action === 'string') return value.action;
    }

    return '';
  };

  const sendMessage = async (overrideMessage = null, options = {}) => {
    const { searchContext = null, suppressUserEcho = false } = options;
    const isEventLikeMessage =
      overrideMessage &&
      typeof overrideMessage === 'object' &&
      (typeof overrideMessage.preventDefault === 'function' || 'nativeEvent' in overrideMessage);
    const messageSource = isEventLikeMessage ? inputValue : overrideMessage ?? inputValue;
    const messageToSend = normalizeMessageInput(messageSource).trim();
    if (!messageToSend || !sessionId || isLoading) return;

    // NOTE: typed messages used to be intercepted here by a hardcoded
    // animal-type → use-case stepper, which asked its own scripted questions and
    // never reached the model. The agent now decides when a clarifying question
    // is warranted, so the interception is gone; the quick-action buttons below
    // simply seed a first message.
    const userMessage = messageToSend;
    const messageSentAt = new Date().toISOString();
    setInputValue('');
    setShowStarterButtons(false);

    // Add user message immediately unless already added by a guided flow step.
    if (!suppressUserEcho) {
      addMessage('user', userMessage);
    }

    // Show typing indicator
    setIsTyping(true);

    try {
      setIsLoading(true);

      // Preferred path: stream the turn so tool progress and listing cards can
      // appear before the assistant has finished writing.
      let streamed = false;
      let handled = false;

      try {
        streamed = await streamAgentTurn({
          sessionId,
          message: messageToSend,
          onEvent: event => {
            if (event.type === 'tool') {
              setToolStatus(
                event.status === 'running' ? TOOL_STATUS_LABELS[event.tool] || null : null
              );
              return;
            }

            if (event.type === 'message') {
              handled = true;
              setToolStatus(null);
              setIsTyping(false);
              addMessage(
                'bot',
                event.text,
                event.attachments || [],
                false,
                event.followUpContext || null
              );
              return;
            }

            if (event.type === 'error') {
              handled = true;
              setToolStatus(null);
              setIsTyping(false);
              addMessage('bot', event.text || 'Sorry, I encountered an error. Please try again.');
            }
          },
        });
      } catch (streamError) {
        console.warn('Chatbot streaming failed, falling back to request/response:', streamError);
        streamed = false;
      }

      if (streamed) {
        clearAsyncResponseRecovery();
        if (!handled) {
          // Stream ended without a final message (e.g. connection dropped
          // mid-turn); recover the persisted reply from history.
          startAsyncResponseRecovery(messageSentAt);
        }
        return;
      }

      setToolStatus(null);

      const response = await fetch(`${apiBaseUrl()}/api/chatbot/message`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          message: messageToSend,
          searchContext,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // isAsync means the n8n engine will answer via Socket.IO/webhook.
        if (data.response && data.response.isAsync) {
          startAsyncResponseRecovery(messageSentAt);
        } else if (data.response && data.response.text) {
          clearAsyncResponseRecovery();
          setIsTyping(false);
          addMessage(
            'bot',
            data.response.text,
            data.response.attachments,
            false,
            data.response.followUpContext || null
          );
        }
      } else {
        clearAsyncResponseRecovery();
        setIsTyping(false);
        addMessage('bot', 'Sorry, I encountered an error. Please try again.');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      clearAsyncResponseRecovery();
      setIsTyping(false);
      addMessage('bot', "Sorry, I'm having trouble processing your message. Please try again.");
    } finally {
      setToolStatus(null);
      setIsLoading(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = action => {
    const actionMessages = {
      book_animal_actors: 'Help me book animal actors',
      find_pet_creators: 'Help me find pet creators',
      view_casting_calls: 'Find casting calls',
      search_trainers: 'Show me pet trainers in my area',
      search_sitters: 'Find pet sitters near me',
      search_wranglers: 'Find pet sitters near me',
      browse_all: 'Show me all available services',
      my_bookings: 'Show my current bookings',
      check_availability: 'Check availability for this week',
      contact_support: 'I need to contact customer support',
    };

    const message = actionMessages[action] || action;
    setShowStarterButtons(false);
    sendMessage(message);
  };

  const handleSuggestedQuestion = questionAction => {
    setShowStarterButtons(false);
    sendMessage(questionAction);
  };

  const handleStartOver = async () => {
    clearAsyncResponseRecovery();
    setIsTyping(false);
    setInputValue('');
    setMessages([]);
    setShowStarterButtons(true);

    try {
      if (sessionId) {
        await fetch(`${apiBaseUrl()}/api/chatbot/end/${sessionId}`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: 'start_over' }),
        });
      }
    } catch (error) {
      console.warn('Failed to end previous chat session during start over:', error);
    }

    try {
      window.localStorage.removeItem(getStorageKey(currentUser));
    } catch (error) {
      // ignore localStorage errors
    }

    setSessionId(null);
  };

  const handleFollowUpQuestion = questionAction => {
    if (questionAction === 'contact_the_animal_list') {
      const lastBotMessageIndex = [...messages]
        .map((msg, idx) => ({ msg, idx }))
        .filter(({ msg }) => msg.type === 'bot')
        .pop()?.idx;

      setFeedbackModal({
        isOpen: true,
        messageIndex: typeof lastBotMessageIndex === 'number' ? lastBotMessageIndex : 0,
      });
      setFeedbackForm({
        email: currentUser?.attributes?.email || '',
        phone: currentUser?.attributes?.profile?.phoneNumber || '',
        message: 'I need help sourcing animal talent. Animal type: , Location: , Project type: ',
      });
      return;
    }

    if (questionAction === 'post_a_casting_call') {
      window.open('/casting-call/new', '_blank', 'noopener,noreferrer');
      return;
    }

    if (questionAction === 'start_a_new_search') {
      handleStartOver();
      return;
    }

    if ((questionAction || '').toLowerCase() === 'start over') {
      handleStartOver();
      return;
    }

    sendMessage(questionAction);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTimestamp = timestamp => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleFeedback = (messageIndex, isHelpful) => {
    setMessageFeedback(prev => ({ ...prev, [messageIndex]: isHelpful }));

    if (!isHelpful) {
      // Open feedback form modal
      setFeedbackModal({ isOpen: true, messageIndex });
      setFeedbackForm({
        email: currentUser?.attributes?.email || '',
        phone: currentUser?.attributes?.profile?.phoneNumber || '',
        message: '',
      });
    }
  };

  const closeFeedbackModal = () => {
    setFeedbackModal({ isOpen: false, messageIndex: null });
    setFeedbackForm({ email: '', phone: '', message: '' });
  };

  const submitFeedback = async () => {
    if (!feedbackForm.message.trim()) {
      alert('Please provide details about your issue.');
      return;
    }

    setFeedbackSubmitting(true);

    try {
      const messageContext = messages[feedbackModal.messageIndex];

      const response = await fetch(`${apiBaseUrl()}/api/chatbot/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          email: feedbackForm.email,
          phone: feedbackForm.phone,
          message: feedbackForm.message,
          messageContext: messageContext.content,
          timestamp: messageContext.timestamp,
        }),
      });

      const data = await response.json();

      if (data.success) {
        addMessage(
          'bot',
          'Thank you for your feedback! Our team will review your message and get back to you soon.',
          [],
          true
        );
        closeFeedbackModal();
      } else {
        alert('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const renderMessage = (message, index) => {
    const isBot = message.type === 'bot';
    const messageAttachments = Array.isArray(message.attachments) ? message.attachments : [];
    const messageClass = classNames(css.message, {
      [css.userMessage]: !isBot,
      [css.botMessage]: isBot,
    });

    // Find the index of the last bot message (excluding typing indicator)
    const lastBotMessageIndex = messages
      .map((msg, idx) => ({ msg, idx }))
      .filter(({ msg }) => msg.type === 'bot')
      .pop()?.idx;

    // Only show feedback buttons on the last bot message (excluding system messages)
    const showFeedbackButtons =
      isBot &&
      index === lastBotMessageIndex &&
      !messageAttachments.some(a => a.type === 'quick_actions') &&
      !message.noFeedback;

    return (
      <div key={index} className={messageClass}>
        <div className={css.messageContent}>
          <div
            className={css.messageText}
            dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content) }}
          />
          {messageAttachments.length > 0 && (
            <div className={css.attachments}>
              {messageAttachments.map((attachment, idx) => (
                <div key={idx} className={css.attachment}>
                  {attachment.type === 'listing_cards' && (
                    <ChatbotListingCards
                      listings={Array.isArray(attachment.data) ? attachment.data : []}
                    />
                  )}
                  {attachment.type === 'quick_actions' &&
                    ((showStarterButtons || attachment?.metadata?.kind === 'guided_search') && (
                      <div className={css.quickActions}>
                        {(Array.isArray(attachment.data) ? attachment.data : []).map(
                          (action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={css.quickActionButton}
                              onClick={() => handleQuickAction(action.action)}
                            >
                              {action.text}
                            </button>
                          )
                        )}
                      </div>
                    ))}
                </div>
              ))}
            </div>
          )}
          <div className={css.timestamp}>{formatTimestamp(message.timestamp)}</div>
          {showFeedbackButtons && (
            <div className={css.feedbackButtons}>
              <span className={css.feedbackLabel}>Was this helpful?</span>
              <button
                className={classNames(css.feedbackButton, {
                  [css.feedbackActive]: messageFeedback[index] === true,
                })}
                onClick={() => handleFeedback(index, true)}
                disabled={messageFeedback[index] !== undefined}
              >
                👍 Yes
              </button>
              <button
                className={classNames(css.feedbackButton, {
                  [css.feedbackActive]: messageFeedback[index] === false,
                })}
                onClick={() => handleFeedback(index, false)}
                disabled={messageFeedback[index] !== undefined}
              >
                👎 No
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) {
    return (
      <button className={css.chatToggleButton} onClick={onToggle}>
        <FormattedMessage id="ChatbotWidget.toggleButton" defaultMessage="Chat with us" />
        <div className={css.chatIcon}>💬</div>
      </button>
    );
  }

  return (
    <div className={css.chatWidget}>
      <div className={css.chatHeader}>
        <div className={css.headerContent}>
          <div className={css.headerTitle}>
            <FormattedMessage
              id="ChatbotWidget.title"
              defaultMessage="Welcome to The Animal List"
            />
          </div>
          <div className={css.headerSubtitle}>
            <FormattedMessage
              id="ChatbotWidget.subtitle"
              defaultMessage="How can I help you today?"
            />
          </div>
        </div>
        <button className={css.closeButton} onClick={onToggle}>
          ×
        </button>
      </div>

      <div className={css.chatMessages}>
        {(() => {
          const lastMessage = messages[messages.length - 1];
          const contextualFollowUpQuestions = getContextualFollowUpQuestions(lastMessage);
          const canShowFollowUpButtons =
            messages.length > 0 &&
            !showStarterButtons &&
            !isLoading &&
            !isTyping &&
            lastMessage &&
            lastMessage.type === 'bot' &&
            contextualFollowUpQuestions.length > 0;

          return (
            <>
              {isLoading && messages.length === 0 && (
                <div className={css.loadingMessage}>
                  <FormattedMessage id="ChatbotWidget.loading" defaultMessage="Connecting..." />
                </div>
              )}

              {/* Suggested Questions - Show when no messages yet */}
              {messages.length === 0 && showStarterButtons && !isLoading && (
                <div className={css.suggestedQuestionsContainer}>
                  <div className={css.suggestedQuestionsTitle}>
                    <FormattedMessage
                      id="ChatbotWidget.suggestedQuestions"
                      defaultMessage="Here are some things I can help you with:"
                    />
                  </div>
                  <div className={css.suggestedQuestions}>
                    {SUGGESTED_QUESTIONS.map((question, idx) => (
                      <button
                        key={idx}
                        className={css.suggestedQuestionButton}
                        onClick={() => handleSuggestedQuestion(question.action)}
                      >
                        {question.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map(renderMessage)}

              {canShowFollowUpButtons && (
                <div className={css.suggestedQuestionsContainer}>
                  <div className={css.suggestedQuestionsTitle}>Next steps:</div>
                  <div className={css.suggestedQuestions}>
                    {contextualFollowUpQuestions.map((question, idx) => (
                      <button
                        key={idx}
                        className={css.suggestedQuestionButton}
                        onClick={() => handleFollowUpQuestion(question.action)}
                      >
                        {question.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isTyping && (
                <div className={css.typingIndicator}>
                  <div className={css.typingDots}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  {/* Show tool status if active, or cycle through rotating status messages */}
                  {toolStatus ? toolStatus : ROTATING_STATUS_MESSAGES[typingStatusIndex]}
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          );
        })()}
      </div>

      <div className={css.chatInput}>
        <div className={css.inputContainer}>
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={e => setInputValue(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className={css.messageInput}
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
            className={css.sendButton}
          >
            <FormattedMessage id="ChatbotWidget.send" defaultMessage="Send" />
          </button>
        </div>
        <div className={css.characterCount}>
          {inputValue.length}/{MAX_MESSAGE_LENGTH}
        </div>
      </div>

      {/* Feedback Modal */}
      {feedbackModal.isOpen && (
        <div className={css.feedbackModal}>
          <div className={css.feedbackModalContent}>
            <div className={css.feedbackModalHeader}>
              <h3>We're sorry to hear that!</h3>
              <button className={css.feedbackModalClose} onClick={closeFeedbackModal}>
                ×
              </button>
            </div>
            <p className={css.feedbackModalDescription}>
              Please share your contact details and let us know how we can help you better.
            </p>
            <div className={css.feedbackForm}>
              <div className={css.formGroup}>
                <label>Email Address *</label>
                <input
                  type="email"
                  value={feedbackForm.email}
                  onChange={e => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                  placeholder="your.email@example.com"
                  required
                />
              </div>
              <div className={css.formGroup}>
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={feedbackForm.phone}
                  onChange={e => setFeedbackForm({ ...feedbackForm, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div className={css.formGroup}>
                <label>How can we help? *</label>
                <textarea
                  value={feedbackForm.message}
                  onChange={e => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                  placeholder="Please describe your issue or question in detail..."
                  rows={4}
                  required
                />
              </div>
              <div className={css.feedbackModalActions}>
                <button
                  className={css.feedbackCancelButton}
                  onClick={closeFeedbackModal}
                  disabled={feedbackSubmitting}
                >
                  Cancel
                </button>
                <button
                  className={css.feedbackSubmitButton}
                  onClick={submitFeedback}
                  disabled={feedbackSubmitting || !feedbackForm.email || !feedbackForm.message}
                >
                  {feedbackSubmitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ChatbotWidget.propTypes = {
  isOpen: bool.isRequired,
  onToggle: func.isRequired,
  currentUser: object,
};

export default ChatbotWidget;
